import { useMemo, useState } from 'react'
import { useGroupsTree } from '../../api/hooks'
import { flattenGroupTree } from '../../pages/Groups'

/** Searchable tree multi-select for group membership (the person page's
 *  Groups card): the full group tree flattened with `— ` depth labels, a
 *  filter box, one checkbox per node (🔒 marks a grant-inheritance barrier).
 *  Full-replace semantics — `selected`/`onChange` carry the uid list the
 *  caller PUTs. */
export function GroupTreePicker({
  selected,
  onChange,
}: {
  selected: string[]
  onChange: (uids: string[]) => void
}) {
  const tree = useGroupsTree()
  const [query, setQuery] = useState('')
  const flat = useMemo(() => flattenGroupTree(tree.data ?? []), [tree.data])
  const needle = query.trim().toLowerCase()
  const visible = needle === '' ? flat : flat.filter((o) => o.name.toLowerCase().includes(needle))

  const toggle = (uid: string) =>
    onChange(selected.includes(uid) ? selected.filter((u) => u !== uid) : [...selected, uid])

  return (
    <div className="flex flex-col gap-1.5">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Filter groups…"
        aria-label="Filter groups"
        className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
      />
      <div className="flex max-h-56 flex-col gap-0.5 overflow-y-auto rounded-sm border border-ostrel-line bg-ostrel-card px-2 py-1.5">
        {visible.length === 0 ? (
          <p className="px-1.5 py-1 text-[11px] text-ostrel-ink-muted">
            {flat.length === 0 ? 'No groups.' : 'No groups match the filter.'}
          </p>
        ) : (
          visible.map((o) => (
            <label
              key={o.uid}
              title={o.barrier ? "Requires direct assignment — grants above don't inherit in" : undefined}
              className="flex items-center gap-2 rounded-sm px-1.5 py-1 text-sm text-ostrel-ink hover:bg-ostrel-sand"
            >
              <input
                type="checkbox"
                checked={selected.includes(o.uid)}
                onChange={() => toggle(o.uid)}
                className="accent-ostrel-gold"
              />
              <span className="tnum min-w-0 truncate">
                {'— '.repeat(o.depth)}
                {o.name}
                {o.barrier ? ' 🔒' : ''}
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  )
}
